import { useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';
import SundaySchoolLayout from '../SundaySchoolLayout';
import {
  canManageDepartment,
  getSelectedDepartment,
  getSelectedDepartmentId,
  getSundaySchoolSession,
  isChurchWideRole,
  saveSundaySchoolSession,
  staffRoleOf,
} from '../sundaySchoolSession';

type ClassGroup = {
  id: number;
  name: string;
  icon: string;
  studentCount: number;
  departmentId: number | null;
};

type Department = { id: number; name: string; sortOrder: number };

export default function SundaySchoolChurch() {
  const session = getSundaySchoolSession();
  const isAdmin = staffRoleOf(session) === '관리자';
  // 교회 정보는 관리자(교회 최종 담당자)만, 부서 관리·반의 부서 이동은 관리자·전체담당자,
  // 반 추가·이름 변경·삭제는 부서담당자 이상.
  const canManageDepartments = isChurchWideRole(staffRoleOf(session));
  const canManageClasses = canManageDepartment(session);
  const currentDepartment = getSelectedDepartment();
  const [churchName, setChurchName] = useState('');
  const [departments, setDepartments] = useState<Department[]>([]);
  const [newDepartmentName, setNewDepartmentName] = useState('');
  const [classes, setClasses] = useState<ClassGroup[]>([]);
  const [newClassName, setNewClassName] = useState('');
  const [saved, setSaved] = useState(false);

  const identity = () =>
    session
      ? {
          authChurch: session.user.authChurch,
          departmentId: getSelectedDepartmentId(),
          userId: session.user.id,
        }
      : null;

  const load = () => {
    const params = identity();
    if (!params) return;
    axios
      .get(`${MainURL}/sundayschool/church`, { params })
      .then(({ data }) => {
        if (data.ok) {
          setChurchName(data.data.name);
          setDepartments(data.data.departments);
          setClasses(data.data.classes);
        }
      })
      .catch((err) => console.error('GET /sundayschool/church', err));
  };

  // 부서를 바꾸면 사이드바 부서 선택기도 다시 불러와야 해서 페이지를 새로 고친다.
  const afterDepartmentChange = (data: { ok: boolean; message?: string }) => {
    if (data.ok) window.location.reload();
    else if (data.message) alert(data.message);
  };

  const addDepartment = async () => {
    const name = newDepartmentName.trim();
    const body = identity();
    if (!name || !body) return;
    try {
      const { data } = await axios.post(`${MainURL}/sundayschool/church/departments`, { ...body, name });
      afterDepartmentChange(data);
    } catch (err) {
      console.error('POST /sundayschool/church/departments', err);
    }
  };

  const renameDepartment = async (d: Department, name: string) => {
    const body = identity();
    if (!body || !name.trim() || name.trim() === d.name) return;
    try {
      const { data } = await axios.patch(`${MainURL}/sundayschool/church/departments/${d.id}`, { ...body, name });
      afterDepartmentChange(data);
    } catch (err) {
      console.error('PATCH /sundayschool/church/departments/:id', err);
    }
  };

  const removeDepartment = async (d: Department) => {
    const body = identity();
    if (!body || !window.confirm(`'${d.name}' 부서를 삭제할까요?`)) return;
    try {
      const { data } = await axios.delete(`${MainURL}/sundayschool/church/departments/${d.id}`, { data: body });
      afterDepartmentChange(data);
    } catch (err) {
      console.error('DELETE /sundayschool/church/departments/:id', err);
    }
  };

  const moveClass = async (c: ClassGroup, moveToDepartmentId: number) => {
    const body = identity();
    const target = departments.find((d) => d.id === moveToDepartmentId);
    if (!body || !target) return;
    if (!window.confirm(`'${c.name}' 반을 학생 ${c.studentCount}명과 함께 '${target.name}' 부서로 옮길까요?`)) return;
    try {
      const { data } = await axios.patch(`${MainURL}/sundayschool/church/classes/${c.id}`, {
        ...body,
        moveToDepartmentId,
      });
      if (data.ok) load();
      else if (data.message) alert(data.message);
    } catch (err) {
      console.error('PATCH /sundayschool/church/classes/:id', err);
    }
  };

  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const renameClass = (id: number, name: string) => {
    setClasses((prev) => prev.map((c) => (c.id === id ? { ...c, name } : c)));
  };

  const commitRename = async (c: ClassGroup) => {
    if (!session) return;
    try {
      await axios.patch(`${MainURL}/sundayschool/church/classes/${c.id}`, {
        ...identity(),
        name: c.name,
      });
    } catch (err) {
      console.error('PATCH /sundayschool/church/classes/:id', err);
    }
  };

  const removeClass = async (id: number) => {
    if (!session) return;
    try {
      const { data } = await axios.delete(`${MainURL}/sundayschool/church/classes/${id}`, {
        data: identity(),
      });
      if (data.ok) setClasses((prev) => prev.filter((c) => c.id !== id));
      else if (data.message) alert(data.message);
    } catch (err) {
      console.error('DELETE /sundayschool/church/classes/:id', err);
    }
  };

  const addClass = async () => {
    const name = newClassName.trim();
    if (!name || !session) return;
    try {
      const { data } = await axios.post(`${MainURL}/sundayschool/church/classes`, {
        ...identity(),
        name,
      });
      if (data.ok) {
        setNewClassName('');
        load();
      }
    } catch (err) {
      console.error('POST /sundayschool/church/classes', err);
    }
  };

  const onSave = async () => {
    if (!session) return;
    try {
      const { data } = await axios.patch(`${MainURL}/sundayschool/church`, {
        ...identity(),
        name: churchName,
      });
      if (data.ok) {
        saveSundaySchoolSession({
          ...session,
          church: { ...session.church, name: churchName },
        });
        setSaved(true);
      }
    } catch (err) {
      console.error('PATCH /sundayschool/church', err);
    }
  };

  return (
    <SundaySchoolLayout footNote="교회·반 설정 · 교회 정보와 반 구성을 관리합니다">
      <div className="admin-church">
        <div className="page-head">
          <div>
            <h1>교회·반 설정 ⛪</h1>
            <div className="sub">교회 기본 정보와 부서·반 구성을 관리하세요</div>
          </div>
        </div>

        <div className="card card-pad mb16">
          <div className="card-title">🏛️ 교회 정보</div>
          <div className="field">
            <label>교회 이름</label>
            <input
              className="input"
              disabled={!isAdmin}
              value={churchName}
              onChange={(e) => {
                setChurchName(e.target.value);
                setSaved(false);
              }}
            />
          </div>
          {isAdmin && (
            <button className="btn btn-primary" onClick={onSave}>
              {saved ? '✅ 저장 완료' : '저장하기'}
            </button>
          )}
        </div>

        <div className="card card-pad mb16">
          <div className="card-title">
            🏷️ 부서 관리 <span className="chip">{departments.length}개</span>
          </div>
          <p className="muted mb16">
            앱을 처음 열면 이 부서 중 하나를 골라 들어갑니다. 반·학생·출석·알림장·교사는 부서별로 나뉘어요.
          </p>
          <div className="row-list">
            {departments.map((d) => (
              <div className="row class-row" key={d.id}>
                <input
                  className="input"
                  defaultValue={d.name}
                  disabled={!canManageDepartments}
                  onBlur={(e) => renameDepartment(d, e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                />
                <span className="muted grow">{d.id === currentDepartment?.id ? '지금 보고 있는 부서' : ''}</span>
                {canManageDepartments && departments.length > 1 && (
                  <button className="btn btn-outline btn-sm" onClick={() => removeDepartment(d)}>
                    삭제
                  </button>
                )}
              </div>
            ))}
          </div>
          {canManageDepartments && (
            <div className="between mt16">
              <input
                className="input"
                placeholder="새 부서 이름 (예: 초등부)"
                value={newDepartmentName}
                onChange={(e) => setNewDepartmentName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addDepartment()}
              />
              <button className="btn btn-sage" onClick={addDepartment}>
                + 부서 추가
              </button>
            </div>
          )}
        </div>

        <div className="card card-pad">
          <div className="card-title">
            📚 {currentDepartment?.name ?? ''} 반 관리 <span className="chip">{classes.length}개</span>
          </div>
          <div className="row-list">
            {classes.map((c) => (
              <div className="row class-row" key={c.id}>
                <input
                  className="input"
                  value={c.name}
                  disabled={!canManageClasses}
                  onChange={(e) => renameClass(c.id, e.target.value)}
                  onBlur={() => commitRename(c)}
                />
                <span className="muted grow">학생 {c.studentCount}명</span>
                {canManageDepartments && departments.length > 1 && (
                  <select
                    className="select move-select"
                    value=""
                    onChange={(e) => e.target.value && moveClass(c, Number(e.target.value))}
                  >
                    <option value="">다른 부서로 옮기기</option>
                    {departments
                      .filter((d) => d.id !== c.departmentId)
                      .map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name}
                        </option>
                      ))}
                  </select>
                )}
                {canManageClasses && (
                  <button className="btn btn-outline btn-sm" onClick={() => removeClass(c.id)}>
                    삭제
                  </button>
                )}
              </div>
            ))}
          </div>
          {canManageClasses && (
            <div className="between mt16">
              <input
                className="input"
                placeholder="새 반 이름 (예: 1반)"
                value={newClassName}
                onChange={(e) => setNewClassName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addClass()}
              />
              <button className="btn btn-sage" onClick={addClass}>
                + 반 추가
              </button>
            </div>
          )}
        </div>
      </div>
    </SundaySchoolLayout>
  );
}
